import React, { useCallback, useEffect, useState } from 'react';
import { ActionIcon, Center, Group, Loader, Stack, Text, Tooltip } from '@mantine/core';
import { TABLE_ACTION_TOOLTIP_PROPS, TooltipTarget } from './TableActionIcon';
import { IconInfoCircle } from '@tabler/icons-react';
import JlfcModal from './JlfcModal';
import { CancelButton } from './FormActionButtons';
import { JLFC_DETAIL_MODAL_SIZE } from './jlfcModalConfig';
import { renderSimpleMarkdown } from '../lib/renderSimpleMarkdown';
import { authFetch, parseJsonResponse } from '../lib/api';

export default function PageHelpButton({
  moduleKey,
  route,
  hasHelp,
  helpTitle,
  modalTitle = 'Aide',
  tooltip = 'Afficher l\'aide',
}) {
  const [available, setAvailable] = useState(hasHelp === true);
  const [checking, setChecking] = useState(hasHelp !== true && hasHelp !== false);
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState('');
  const [title, setTitle] = useState(helpTitle || modalTitle);
  const [error, setError] = useState('');

  useEffect(() => {
    if (helpTitle) setTitle(helpTitle);
  }, [helpTitle]);

  useEffect(() => {
    if (hasHelp === true) {
      setAvailable(true);
      setChecking(false);
      return undefined;
    }
    if (hasHelp === false) {
      setAvailable(false);
      setChecking(false);
      return undefined;
    }

    let cancelled = false;

    async function checkAvailability() {
      if (!route && !moduleKey) {
        setAvailable(false);
        setChecking(false);
        return;
      }

      setChecking(true);
      try {
        const params = route
          ? `path=${encodeURIComponent(route)}`
          : `moduleKey=${encodeURIComponent(moduleKey)}`;
        const res = await authFetch(`/api/modules/help/availability?${params}`);
        const data = await parseJsonResponse(res);
        if (cancelled) return;
        if (res.ok && data.success && data.data?.has_help) {
          setAvailable(true);
          if (data.data.help_title) setTitle(data.data.help_title);
        } else {
          setAvailable(false);
        }
      } catch {
        if (!cancelled) setAvailable(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    }

    checkAvailability();
    return () => {
      cancelled = true;
    };
  }, [route, moduleKey, hasHelp]);

  const loadHelp = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const url = route
        ? `/api/modules/help/by-route?path=${encodeURIComponent(route)}`
        : `/api/modules/${moduleKey}/help`;
      const res = await authFetch(url);
      const data = await parseJsonResponse(res);
      if (!res.ok || !data.success) throw new Error(data.message || 'Notice introuvable');
      setContent(data.data.help_markdown || '');
      setTitle(data.data.help_title || modalTitle);
    } catch (err) {
      setError(err.message || 'Impossible de charger la notice.');
      setContent('');
    } finally {
      setLoading(false);
    }
  }, [moduleKey, route, modalTitle]);

  const handleOpen = () => {
    setOpened(true);
    if (!content && !error) loadHelp();
  };

  if (checking || !available) return null;

  return (
    <>
      <Tooltip label={tooltip} {...TABLE_ACTION_TOOLTIP_PROPS}>
        <TooltipTarget>
          <ActionIcon
            variant="light"
            color="gray"
            size="lg"
            radius="md"
            aria-label={tooltip}
            onClick={handleOpen}
          >
            <IconInfoCircle size={20} stroke={1.6} />
          </ActionIcon>
        </TooltipTarget>
      </Tooltip>

      <JlfcModal
        opened={opened}
        onClose={() => setOpened(false)}
        title={title}
        size={JLFC_DETAIL_MODAL_SIZE}
      >
        <Stack gap="md">
          {loading ? (
            <Center py="xl">
              <Loader size="sm" />
            </Center>
          ) : error ? (
            <Text c="red" size="sm">
              {error}
            </Text>
          ) : (
            <Stack gap="xs">{renderSimpleMarkdown(content)}</Stack>
          )}

          <Group justify="flex-end">
            <CancelButton onClick={() => setOpened(false)}>Fermer</CancelButton>
          </Group>
        </Stack>
      </JlfcModal>
    </>
  );
}
